'use client';

import { useEffect } from 'react';

interface DataLayer extends Array<Record<string, unknown>> {
  push(args: Record<string, unknown>): number;
}

interface WindowWithDataLayer extends Window {
  dataLayer?: DataLayer;
}

export default function SignUpOriginScript() {
  useEffect(() => {
    const initOriginTracking = async () => {
      // 1) Try the cookie first
      const match = document.cookie.match(/(?:^|; )sg_origin=([^;]+)/);
      let origin = match ? match[1] : null;

      // 2) If no cookie, parse the `r=` query param
      if (!origin) {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('r');
        if (returnUrl) {
          try {
            const parsed = new URL(returnUrl);
            const host = parsed.hostname.toLowerCase();
            if (host.includes('auctions.signupgenius')) {
              origin = 'auctions';
            } else if (host.includes('tickets.signupgenius')) {
              origin = 'tickets';
            } else if (host.includes('donations.signupgenius')) {
              origin = 'donations';
            } else if (host.includes('m.signupgenius')) {
              origin = 'm';
            } else {
              origin = 'signupgenius';
            }
          } catch {
            origin = 'signupgenius';
          }
        }
      }

      // 3) Default if still empty
      origin = origin || 'signupgenius';

      // Wait for sourcepoint to be available
      const waitForSourcePoint = () => {
        return new Promise<void>((resolve, reject) => {
          const MAX_WAIT_TIME = 5000; // 5 seconds
          const startTime = Date.now();

          const check = () => {
            if (window['sourcepoint/utility']) {
              resolve();
            } else if (Date.now() - startTime >= MAX_WAIT_TIME) {
              reject(new Error('Timeout waiting for sourcepoint/utility'));
            } else {
              setTimeout(check, 100);
            }
          };
          check();
        });
      };

      try {
        await waitForSourcePoint();

        // Check if Google Analytics is allowed
        const gaConsent =
          await window['sourcepoint/utility'].isVendorAllowed(
            'googleAnalytics'
          );
        if (gaConsent.allowed) {
          // Initialize dataLayer safely only if consent is given
          const typedWindow = window as WindowWithDataLayer;
          typedWindow.dataLayer = typedWindow.dataLayer || [];
          typedWindow.dataLayer.push({
            sg_origin: origin,
          });
        }
      } catch (error) {
        console.error(
          'Error checking consent or initializing tracking:',
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
    };

    initOriginTracking();
  }, []); // Run once on mount

  return null;
}
